import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Moon, Tent, Compass, Telescope } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { testimonials } from '../data/mock';
import StarField from '../components/StarField';
import TonightsSky from '../components/TonightsSky';
import logoUrl from '../assets/logo.png';

export const Home = () => {
  const features = [
    {
      icon: Telescope,
      title: 'Stargaze From The Deck',
      description:
        'Our two-story observation deck overlooks open land with zero light pollution — meteors, milky way, and a moon that feels close enough to touch.',
    },
    {
      icon: Tent,
      title: 'Stay Under The Sky',
      description:
        '8 RV sites with 30/50 amp service and primitive campsites tucked into the pines. Bring your rig — or just a tent and a sleeping bag.',
    },
    {
      icon: Compass,
      title: 'Rent The Gear',
      description:
        'Trailers, generators, tractors. Neighbor-to-neighbor rentals at fair rates so your adventure gets the right kit.',
    },
  ];

  const thingsToDo = [
    {
      title: 'The Observation Deck',
      description:
        'Two-story deck under an open sky — morning coffee, evening cocktails, late-night meteor counts and lazy movie nights.',
      image: 'https://images.unsplash.com/photo-1596470693312-9a3686a0af0f?auto=format&fit=crop&w=1000&q=80',
    },
    {
      title: 'ATV & Hiking Trails',
      description:
        'Easy walks and rugged terrain — bridge crossings, logging roads, and pineywood paths that lead somewhere quiet.',
      image: 'https://images.unsplash.com/photo-1625495194343-f2d69dae42fb?auto=format&fit=crop&w=1000&q=80',
    },
    {
      title: 'Thoughtful Campsites',
      description:
        'Well-kept sites with access to modern amenities — equally good for the primitive camper and the comfort lover.',
      image: 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?auto=format&fit=crop&w=1000&q=80',
    },
    {
      title: 'Hammock Fields',
      description:
        'Hammocks scattered between the pines — take a breath, unplug, and sleep beneath a sky full of stars.',
      image: 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?auto=format&fit=crop&w=1000&q=80',
    },
  ];

  return (
    <div className="min-h-screen bg-[#050814]">
      {/* ============ HERO ============ */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated star field (moon removed per user feedback) */}
        <StarField count={300} showMoon={false} />

        {/* Subtle ground gradient at bottom of hero */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#050814] via-[#050814]/80 to-transparent z-10"
        />

        <div className="container mx-auto px-4 z-20 text-center text-[#F4F1EA] py-32">
          {/* Logo */}
          <img
            src={logoUrl}
            alt="Creek Bottom Ranch"
            className="w-44 md:w-56 lg:w-64 h-auto mx-auto mb-8 rise-in drop-shadow-[0_0_50px_rgba(212,175,55,0.35)]"
          />

          <p
            data-testid="hero-eyebrow"
            className="rise-in delay-1 uppercase tracking-[0.5em] text-[10px] md:text-xs text-[#D4AF37] mb-5 sr-only"
          >
            Creek Bottom Ranch · Est. 2025
          </p>

          <h1
            data-testid="hero-headline"
            className="rise-in delay-1 font-display text-5xl md:text-7xl lg:text-[5.5rem] font-medium leading-[1.04] mb-6 tracking-tight text-[#F4F1EA] text-glow-gold"
          >
            Take a Trip <span className="italic text-[#E5C158]">to&nbsp;the&nbsp;Stars</span>
          </h1>

          <p
            data-testid="hero-subhead"
            className="rise-in delay-2 text-lg md:text-xl text-[#F4F1EA]/80 max-w-2xl mx-auto mb-10 leading-relaxed"
          >
            An intimate stargazing ranch in East Texas — eight RV sites, primitive camping,
            and a two-story deck under one of the darkest skies in the South.
          </p>

          <div className="rise-in delay-3 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/booking">
              <Button
                data-testid="home-book-stay-btn"
                size="lg"
                className="bg-[#B8860B] hover:bg-[#D4AF37] text-[#050814] font-semibold text-base md:text-lg px-8 py-6 rounded-full shadow-[0_0_38px_rgba(184,134,11,0.45)] hover:shadow-[0_0_55px_rgba(212,175,55,0.65)] transition-all duration-300 hover:scale-[1.03]"
              >
                Book Your Stay Under the Stars
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <Link to="/equipment">
              <Button
                data-testid="home-browse-equipment-btn"
                size="lg"
                variant="outline"
                className="bg-transparent border border-[#B8860B] text-[#E5C158] hover:bg-[#B8860B]/10 hover:text-[#F4F1EA] font-medium text-base md:text-lg px-8 py-6 rounded-full transition-all duration-300"
              >
                Explore the Ranch
              </Button>
            </Link>
          </div>
        </div>

        {/* Scroll cue */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 animate-bounce">
          <div className="w-6 h-10 border border-[#B8860B]/70 rounded-full flex justify-center">
            <div className="w-1 h-2 bg-[#D4AF37] rounded-full mt-2"></div>
          </div>
        </div>
      </section>

      {/* ============ THREE-UP CELESTIAL VALUE PROPS ============ */}
      <section className="relative py-24 bg-[#050814]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <p className="uppercase tracking-[0.4em] text-[10px] md:text-xs text-[#D4AF37] mb-4">
              What awaits at the ranch
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#F4F1EA] mb-4">
              Quiet land. Open sky. Real rest.
            </h2>
            <p className="text-base md:text-lg text-[#F4F1EA]/65 max-w-2xl mx-auto">
              Bring the rig. Bring the tent. Bring nothing at all — and let the night sky do the work.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
            {features.map((feature, index) => (
              <Card
                key={index}
                data-testid={`home-feature-${index}`}
                className="group relative overflow-hidden border border-[#B8860B]/20 bg-[#0B132B] hover:border-[#B8860B]/50 hover:-translate-y-1 transition-all duration-500 shadow-[0_8px_30px_rgba(0,0,0,0.4)]"
              >
                {/* Inner glow on hover */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-br from-[#B8860B]/[0.06] to-transparent transition-opacity duration-500" />
                <CardContent className="relative p-8 text-center">
                  <div className="relative mx-auto w-16 h-16 mb-5 rounded-full bg-[#050814] border border-[#B8860B]/40 flex items-center justify-center group-hover:border-[#D4AF37] transition-colors">
                    <feature.icon className="text-[#D4AF37]" size={28} />
                    <span className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 shadow-[0_0_25px_rgba(212,175,55,0.45)] transition-opacity duration-500" />
                  </div>
                  <h3 className="font-display text-2xl font-semibold text-[#F4F1EA] mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-[#F4F1EA]/70 leading-relaxed text-sm md:text-base">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TONIGHT'S SKY ============ */}
      <TonightsSky />

      {/* ============ THINGS TO DO ============ */}
      <section className="relative py-24 bg-gradient-to-b from-[#050814] via-[#0B132B] to-[#050814]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <p className="uppercase tracking-[0.4em] text-[10px] md:text-xs text-[#D4AF37] mb-4">
              On the property
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#F4F1EA] mb-4">
              Earth-bound things to love
            </h2>
            <p className="text-base md:text-lg text-[#F4F1EA]/65 max-w-2xl mx-auto">
              While you wait for the stars to come out.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-7">
            {thingsToDo.map((item, idx) => (
              <Card
                key={idx}
                data-testid={`home-thing-to-do-${idx}`}
                className="group overflow-hidden border border-[#B8860B]/20 bg-[#0B132B] shadow-lg hover:shadow-[0_18px_40px_rgba(0,0,0,0.5)] hover:-translate-y-1 hover:border-[#B8860B]/50 transition-all duration-500"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-[1.08] transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050814] via-[#050814]/40 to-transparent"></div>
                </div>
                <CardContent className="p-6">
                  <h3 className="font-display text-xl font-semibold text-[#E5C158] mb-2">
                    {item.title}
                  </h3>
                  <p className="text-[#F4F1EA]/70 text-sm leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section className="relative py-24 bg-[#050814]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <p className="uppercase tracking-[0.4em] text-[10px] md:text-xs text-[#D4AF37] mb-4">
              From travelers who looked up
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#F4F1EA] mb-4">
              Stories from the deck
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
            {testimonials.map((testimonial) => (
              <Card
                key={testimonial.id}
                data-testid={`home-testimonial-${testimonial.id}`}
                className="relative border border-[#B8860B]/20 bg-[#0B132B] shadow-lg hover:border-[#B8860B]/40 transition-colors"
              >
                <CardContent className="p-8">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="text-[#D4AF37] fill-current" size={18} />
                    ))}
                  </div>
                  <p className="text-[#F4F1EA]/80 mb-6 italic leading-relaxed">"{testimonial.text}"</p>
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-[#050814] border border-[#B8860B]/50 rounded-full flex items-center justify-center text-[#D4AF37] font-bold mr-3">
                      {testimonial.avatar}
                    </div>
                    <div>
                      <p className="font-semibold text-[#E5C158]">{testimonial.name}</p>
                      <p className="text-xs text-[#F4F1EA]/55">{testimonial.location}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CTA STRIP ============ */}
      <section className="relative py-24 border-t border-[#B8860B]/15 overflow-hidden">
        {/* Mini star background */}
        <div className="absolute inset-0 opacity-60 pointer-events-none">
          <StarField count={90} showMoon={false} shootingStars={false} />
        </div>

        <div className="relative container mx-auto px-4 text-center">
          <Moon className="mx-auto text-[#D4AF37] mb-5" size={40} strokeWidth={1.2} />
          <h2 className="font-display text-4xl md:text-6xl font-medium text-[#F4F1EA] mb-5 tracking-tight">
            The sky is waiting.
          </h2>
          <p className="text-lg md:text-xl text-[#F4F1EA]/75 mb-9 max-w-2xl mx-auto">
            Eight RV sites. Primitive camping. One unforgettable view above.
          </p>
          <Link to="/booking">
            <Button
              data-testid="home-cta-reserve-btn"
              size="lg"
              className="bg-[#B8860B] hover:bg-[#D4AF37] text-[#050814] font-semibold text-lg px-10 py-6 rounded-full shadow-[0_0_42px_rgba(184,134,11,0.45)] hover:shadow-[0_0_60px_rgba(212,175,55,0.7)] transition-all duration-300 hover:scale-[1.03]"
            >
              Reserve Your Spot Now <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
